import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AgGridReact } from "@ag-grid-community/react";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-quartz.css";
import { ClientSideRowModelModule } from "@ag-grid-community/client-side-row-model";
import { ModuleRegistry } from "@ag-grid-community/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash, faTrash } from "@fortawesome/free-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Spinner from "react-activity/dist/Spinner";
import "react-activity/dist/Spinner.css";
import { apiUrl } from "../../../config/Config";
import { formatIstFullDateTime } from "../../../utils/dateTime";
import PostPreviewModal from "../../common/PostPreviewModal";

ModuleRegistry.registerModules([ClientSideRowModelModule]);

const Posts = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userdata = useSelector((store) => store.userReducer.userdata);
  const [filter, setFilter] = useState("active");
  const [rowData, setRowData] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [showloding, setshowloding] = useState(false);
  const paginationPageSizeSelector = useMemo(() => [10, 20, 50, 100], []);

  const headers = useMemo(() => ({
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + userdata?.token,
    },
  }), [userdata?.token]);

  const handleInvalidUser = useCallback((message) => {
    if (message === "invalid user") {
      dispatch({ type: "userdata", payload: {} });
      navigate("/admin/login");
      return true;
    }
    return false;
  }, [dispatch, navigate]);

  const fetchPosts = useCallback(async () => {
    setshowloding(true);
    await axios
      .get(apiUrl + "admingetposts?status=" + filter, headers)
      .then((res) => {
        if (res.data.status === "success") {
          setRowData(res.data.data || []);
        } else if (!handleInvalidUser(res.data.message)) {
          alert(res.data.message);
        }
      })
      .catch((err) => alert(err.message));
    setshowloding(false);
  }, [filter, handleInvalidUser, headers]);

  const deletePost = async (id) => {
    const result = window.confirm("Remove this post?");
    if (!result) return;

    setshowloding(true);
    await axios
      .post(apiUrl + "admindeletepost", { id }, headers)
      .then((res) => {
        if (res.data.status === "success") {
          fetchPosts();
        } else if (!handleInvalidUser(res.data.message)) {
          alert(res.data.message);
        }
      })
      .catch((err) => alert(err.message));
    setshowloding(false);
  };

  const hidePost = async (id) => {
    const result = window.confirm("Hide/de-activate this post from public feeds?");
    if (!result) return;

    const adminMessage = window.prompt("Message to send to the post owner", "Your post was hidden by admin after review.");
    if (!adminMessage || !adminMessage.trim()) {
      alert("Please add a message before hiding this post.");
      return;
    }

    setshowloding(true);
    await axios
      .post(apiUrl + "adminupdatepostvisibility", { id, visibility: "false", admin_message: adminMessage }, headers)
      .then((res) => {
        if (res.data.status === "success") {
          fetchPosts();
        } else if (!handleInvalidUser(res.data.message)) {
          alert(res.data.message);
        }
      })
      .catch((err) => alert(err.message));
    setshowloding(false);
  };

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const columns = [
    { field: "fullname", headerName: "User", minWidth: 180 },
    { field: "email", headerName: "Email", minWidth: 220 },
    { field: "biotext", headerName: "Post", minWidth: 320 },
    { field: "city", headerName: "City", minWidth: 130 },
    { field: "state", headerName: "State", minWidth: 130 },
    { field: "likes_count", headerName: "Likes", minWidth: 100 },
    { field: "comments_count", headerName: "Comments", minWidth: 120 },
    { field: "reports_count", headerName: "Reports", minWidth: 110 },
    { field: "visibility", headerName: "Visible", minWidth: 110 },
    {
      field: "created_at",
      headerName: "Date",
      minWidth: 160,
      cellRenderer: (props) => props.data.created_at ? formatIstFullDateTime(props.data.created_at) + " IST" : "-",
    },
    {
      field: "action",
      headerName: "Action",
      minWidth: 120,
      cellRenderer: (props) => (
        <div className="admin-grid-actions">
          <button title="View post" onClick={() => setSelectedPost(props.data)}>
            <FontAwesomeIcon icon={faEye} />
          </button>
          <button title="Hide/de-activate post" onClick={() => hidePost(props.data.id)}>
            <FontAwesomeIcon icon={faEyeSlash} />
          </button>
          <button title="Remove post" className="danger" onClick={() => deletePost(props.data.id)}>
            <FontAwesomeIcon icon={faTrash} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="dashboardbox" style={{ width: "100%" }}>
      <div className="productstogglebar mt-2">
        <div className="orderstatustogglediv">
          {[
            ["active", "Active Posts"],
            ["reported", "Reported"],
            ["deleted", "Removed"],
            ["all", "All"],
          ].map(([key, label]) => (
            <div key={key} className={filter === key ? "orderstatusdiv active" : "orderstatusdiv"} onClick={() => setFilter(key)}>
              <p>{label}</p>
            </div>
          ))}
        </div>
        {showloding ? <div className="moderation-loader"><Spinner color="#34A853" size={18} /></div> : null}
      </div>

      <div className="ag-theme-quartz mt-4" style={{ width: "100%", height: 560 }}>
        <AgGridReact
          rowData={rowData}
          columnDefs={columns}
          defaultColDef={{ flex: 1, filter: true, sortable: true, resizable: true }}
          pagination={true}
          paginationPageSize={10}
          paginationPageSizeSelector={paginationPageSizeSelector}
          rowHeight={54}
        />
      </div>
      <PostPreviewModal post={selectedPost} onClose={() => setSelectedPost(null)} />
    </div>
  );
};

export default Posts;
